import styled from '@emotion/styled';
import { color, Icon, typography } from '@uniquegood/realworld-studio-design';

interface ComparisonSection {
  title: string;
  rows: ComparisonRow[];
}

interface ComparisonRow {
  feature: string;
  free: boolean | string;
  pro: boolean | string;
  enterprise: boolean | string;
  class: boolean | string;
}

interface ComparisonTableProps {
  sections: ComparisonSection[];
  onClose: () => void;
}

const ComparisonTable = ({ sections, onClose }: ComparisonTableProps) => {
  const renderCellValue = (value: boolean | string) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Icon icon="check_solid" color={color.palette_primary_purple_100} size="24px" />
      ) : (
        <EmptyCell />
      );
    }
    return <TextValue>{value}</TextValue>;
  };

  return (
    <SectionContainer>
      <ContentWrapper>
        <Title>요금제 비교</Title>

        <TablesWrapper>
          {sections.map((section) => (
            <TableSection key={section.title}>
              <TableWrapper>
                <HeaderRow>
                  <HeaderCell isFirst>{section.title}</HeaderCell>
                  <HeaderCell>무료</HeaderCell>
                  <HeaderCell>프로</HeaderCell>
                  <HeaderCell>엔터프라이즈</HeaderCell>
                  <HeaderCell>클래스</HeaderCell>
                </HeaderRow>

                {section.rows.map((row, rowIndex) => (
                  <DataRow key={row.feature} isLast={rowIndex === section.rows.length - 1}>
                    <DataCell isFeature>
                      {typeof row.feature === 'string' && row.feature.includes('\n') ? (
                        <FeatureContent>
                          {row.feature.split('\n').map((line, i) => {
                            if (line.startsWith('•')) {
                              return (
                                <BulletItem key={line}>
                                  <BulletPoint>•</BulletPoint>
                                  <BulletText>{line.substring(1).trim()}</BulletText>
                                </BulletItem>
                              );
                            }
                            return <FeatureLine key={line}>{line}</FeatureLine>;
                          })}
                        </FeatureContent>
                      ) : (
                        row.feature
                      )}
                    </DataCell>
                    <DataCell>{renderCellValue(row.free)}</DataCell>
                    <DataCell>{renderCellValue(row.pro)}</DataCell>
                    <DataCell>{renderCellValue(row.enterprise)}</DataCell>
                    <DataCell>{renderCellValue(row.class)}</DataCell>
                  </DataRow>
                ))}
              </TableWrapper>
            </TableSection>
          ))}
        </TablesWrapper>

        <CloseButton onClick={onClose}>비교 표 닫기</CloseButton>
      </ContentWrapper>
    </SectionContainer>
  );
};

export default ComparisonTable;

const SectionContainer = styled.section`
  width: 100%;
  background: ${color.white};
  padding: 64px 180px;
  animation: fadeIn 0.3s ease-in;
  margin-top: 40px;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const ContentWrapper = styled.div`
  margin: 0 auto;
`;

const Title = styled.h2`
  font-size: 28px;
  line-height: 32px;
  font-weight: ${typography.weight.bold};
  color: ${color.text_default};
  text-align: center;
  margin: 0 0 64px 0;
`;

const TablesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
`;

const TableSection = styled.div`
  width: 100%;
`;

const TableWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const HeaderRow = styled.div`
  display: flex;
  background: #fafafa;
`;

const HeaderCell = styled.div<{ isFirst?: boolean }>`
  flex: 1;
  padding: 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${({ isFirst }) => (isFirst ? typography.weight.bold : typography.weight.medium)};
  color: ${color.text_default};
  text-align: ${({ isFirst }) => (isFirst ? 'left' : 'center')};
  display: flex;
  align-items: center;
  justify-content: ${({ isFirst }) => (isFirst ? 'flex-start' : 'center')};
`;

const DataRow = styled.div<{ isLast?: boolean }>`
  display: flex;
  background: ${color.white};
  border-bottom: ${({ isLast }) => (isLast ? 'none' : '1px solid #f5f5f5')};
`;

const DataCell = styled.div<{ isFeature?: boolean }>`
  flex: 1;
  padding: 16px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: ${color.text_default};
  display: flex;
  align-items: center;
  justify-content: ${({ isFeature }) => (isFeature ? 'flex-start' : 'center')};
  text-align: ${({ isFeature }) => (isFeature ? 'left' : 'center')};
  min-height: 52px;
`;

const CheckIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #7800ff;
  color: ${color.white};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: ${typography.weight.bold};
`;

const EmptyCell = styled.div`
  width: 24px;
  height: 24px;
`;

const TextValue = styled.div`
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.regular};
  color: ${color.text_default};
  text-align: center;
`;

const FeatureContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const FeatureLine = styled.div`
  line-height: 20px;
`;

const BulletItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-left: 12px;
`;

const BulletPoint = styled.span`
  flex-shrink: 0;
  line-height: 20px;
`;

const BulletText = styled.span`
  flex: 1;
  line-height: 20px;
`;

const CloseButton = styled.button`
  margin: 0 auto;
  padding: 12px 24px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 20px;
  font-weight: ${typography.weight.medium};
  background: ${color.white};
  color: #555555;
  border: 1px solid #bdbdbd;
  cursor: pointer;
  transition: all 0.2s;
  display: block;

  &:hover {
    background: #fafafa;
  }
`;
